import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { Box, Button } from '@mui/material';
import { StyledTabs, StyledTab } from '../styled';
import { SnackbarSeverityType } from '../../../enums';
import FeeFilters from './FeeFilters';
import FeeDetailsTable from './FeeDetailsTable';
import FeeFineDetailsTable from './FeeFineDetailsTable';
import PaymentDetailsTable from './PaymentDetailsTable';
import AddPaymentDialog from '../dialogues/AddPaymentDialog';
import AddConcessionWaiverDialog from '../dialogues/AddConcessionWaiverDialog';
import ApproveRejectConcessionWaiverDialog from '../dialogues/ApproveRejectConcessionWaiverDialog';
import DeletePaymentSchemesDialog from '../dialogues/DeletePaymentSchemesDialog';
import DeletePendingLateFeeDialog from '../dialogues/DeletePendingLateFeeDialog';
import UpdateFeeStatusDialog from '../dialogues/UpdateFeeStatusDialog';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import {
  StudentPaymentSchemeWithMetrics,
  FeeFineDetailsData,
  StudentTransactionMatrixItem
} from '../../../types/paymentScheme';
import {
  superAdminUsers,
  usersWithConcessionApprovalAccess,
  usersWithDeletePaymentSchemeAccess
} from '../../../constants/superAdminUsers';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { generateFeeFineDetails } from '../utils/feeFineDetailsTableColumns';
import { RootState } from '../../../redux/store';
import { normalizeSelection } from '../../../utils/gridSelection';
import { TransformedStudentDetail } from '../../../types/userType';
import CancelTransactionsDialog from '../dialogues/CancelTransactionsDialog';
import CancelPaymentDialog from '../dialogues/CancelPaymentDialog';
import ViewReceiptPdfDialog from '../dialogues/ViewReceiptPdfDialog';
import UpdatePaymentStatusDialog from '../dialogues/UpdatePaymentStatusDialog';
import { downloadFileFromUrl } from '../../../utils/fileGeneration';
import pallet from '../../../layouts/pallet';
import Icons from '../../../components/icons';
import { PageWrapper } from '../../common/styled';

interface FeeManagementSectionProps {
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  studentId: string;
  userDetails: TransformedStudentDetail | null;
  studentPaymentSchemes: StudentPaymentSchemeWithMetrics[];
  studentTransactions: StudentTransactionMatrixItem[];
  isLoadingFeeDetails: boolean;
  fetchFeeDetails: () => Promise<void>;
  academicYear: string;
  setAcademicYear: (value: string) => void;
  componentType: string;
  setComponentType: (value: string) => void;
  particulars: string;
  setParticulars: (value: string) => void;
}

export const FeeManagementSection: React.FC<FeeManagementSectionProps> = ({
  onShowToast,
  studentId,
  userDetails,
  studentPaymentSchemes,
  studentTransactions,
  isLoadingFeeDetails,
  fetchFeeDetails,
  academicYear,
  setAcademicYear,
  componentType,
  setComponentType,
  particulars,
  setParticulars
}) => {
  const { paymentUser } = useSelector((state: RootState) => state.paymentUser);
  const userEmail = paymentUser?.email;

  // Dialog-related state declarations
  const [isAddConcessionWaiverDialogOpen, setIsAddConcessionWaiverDialogOpen] =
    useState(false);
  const [isAddPaymentDialogOpen, setIsAddPaymentDialogOpen] = useState(false);
  const [
    isApproveRejectConcessionWaiverDialogOpen,
    setIsApproveRejectConcessionWaiverDialogOpen
  ] = useState(false);
  const [isCancelPaymentDialogOpen, setIsCancelPaymentDialogOpen] =
    useState(false);
  const [isCancelTransactionsDialogOpen, setIsCancelTransactionsDialogOpen] =
    useState(false);
  const [
    isDeletePaymentSchemesDialogOpen,
    setIsDeletePaymentSchemesDialogOpen
  ] = useState(false);
  const [
    isDeletePendingLateFeeDialogOpen,
    setIsDeletePendingLateFeeDialogOpen
  ] = useState(false);
  const [isUpdateFeeStatusDialogOpen, setIsUpdateFeeStatusDialogOpen] =
    useState(false);
  const [isUpdatePaymentStatusDialogOpen, setIsUpdatePaymentStatusDialogOpen] =
    useState(false);
  const [isViewReceiptDialogOpen, setIsViewReceiptDialogOpen] = useState(false);
  const [pdfUrlToView, setPdfUrlToView] = useState<string | null>(null);
  const [selectedSchemeForApproval, setSelectedSchemeForApproval] =
    useState<StudentPaymentSchemeWithMetrics | null>(null);
  const [
    selectedSchemeForFeeStatusUpdate,
    setSelectedSchemeForFeeStatusUpdate
  ] = useState<StudentPaymentSchemeWithMetrics | null>(null);
  const [
    selectedTransactionForStatusUpdate,
    setSelectedTransactionForStatusUpdate
  ] = useState<StudentTransactionMatrixItem | null>(null);
  const [selectedTransactionToCancel, setSelectedTransactionToCancel] =
    useState<StudentTransactionMatrixItem | null>(null);

  // Other state declarations
  const [activeTab, setActiveTab] = useState(0);
  const [isProcessingReceipt, setIsProcessingReceipt] = useState(false);
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [selectedFeeFineRows, setSelectedFeeFineRows] = useState<string[]>([]);
  const [selectedFeeRows, setSelectedFeeRows] = useState<string[]>([]);
  const [selectedSourceTable, setSelectedSourceTable] = useState<
    'feeDetails' | 'feeFineDetails' | null
  >(null);

  // Derived data and options
  const academicYearOptions = [
    { value: 'All', label: 'All' },
    ...Array.from(
      new Set(studentPaymentSchemes.map((scheme) => scheme.academicYear))
    ).map((year) => ({ value: year, label: year }))
  ];
  const componentTypeOptions = [
    { value: 'All', label: 'All' },
    ...Array.from(
      new Set(studentPaymentSchemes.map((scheme) => scheme.component))
    ).map((component) => ({ value: component, label: component }))
  ];
  const feeDetailsData = studentPaymentSchemes;
  const lateFeeDetails: FeeFineDetailsData[] = generateFeeFineDetails(
    studentPaymentSchemes
  );
  const particularsOptions = [
    { value: 'All', label: 'All' },
    ...Array.from(
      new Set(studentPaymentSchemes.map((scheme) => scheme.particulars))
    ).map((particular) => ({ value: particular, label: particular }))
  ];
  const paymentDetailsData = studentTransactions;

  // Functions (Callbacks, Effects, Handlers - sorted alphabetically)
  const fetchAndHandleReceiptPdf = async (
    transaction: StudentTransactionMatrixItem,
    actionType: 'view' | 'download'
  ) => {
    if (!transaction.receiptNo) {
      onShowToast(
        'Receipt number not available.',
        SnackbarSeverityType.warning
      );
      return;
    }

    setIsProcessingReceipt(true);
    if (actionType === 'view') {
      setIsViewReceiptDialogOpen(true);
      setPdfUrlToView(null);
    }

    try {
      const response = await paymentSchemesApis.fetchReceiptPdfUrl(
        transaction.receiptNo
      );
      const pdfUrl = response.response;
      const fileName =
        response.meta.name || `Receipt-${transaction.receiptNo}.pdf`;

      if (actionType === 'view') {
        setPdfUrlToView(pdfUrl);
      } else {
        downloadFileFromUrl(pdfUrl, fileName, 'pdf');
        onShowToast(
          'Receipt download initiated!',
          SnackbarSeverityType.success
        );
      }
    } catch (error: any) {
      console.error(`Error ${actionType}ing receipt:`, error);
      onShowToast(
        error?.errors?.[0]?.message || `Failed to ${actionType} receipt.`,
        SnackbarSeverityType.error
      );
      if (actionType === 'view') {
        setIsViewReceiptDialogOpen(false);
      }
    } finally {
      setIsProcessingReceipt(false);
    }
  };

  const handleAddConcessionWaiverClick = () => {
    if (selectedFeeRows.length === 1 && selectedFeeFineRows.length === 0) {
      setSelectedSourceTable('feeDetails');
    } else if (
      selectedFeeFineRows.length === 1 &&
      selectedFeeRows.length === 0
    ) {
      setSelectedSourceTable('feeFineDetails');
    } else {
      setSelectedSourceTable(null);
    }
    setIsAddConcessionWaiverDialogOpen(true);
  };

  const handleApproveRejectClick = (
    scheme: StudentPaymentSchemeWithMetrics,
    source: 'feeDetails' | 'feeFineDetails'
  ) => {
    setSelectedSchemeForApproval(scheme);
    setSelectedSourceTable(source);
    setIsApproveRejectConcessionWaiverDialogOpen(true);
  };

  const handleCloseAddConcessionWaiverDialog = () => {
    setIsAddConcessionWaiverDialogOpen(false);
    setSelectedSourceTable(null);
    setSelectedFeeRows([]);
    setSelectedFeeFineRows([]);
  };

  const handleCloseAddPaymentDialog = () => {
    setIsAddPaymentDialogOpen(false);
    setSelectedFeeRows([]);
    setSelectedFeeFineRows([]);
  };

  const handleCloseApproveRejectDialog = () => {
    setIsApproveRejectConcessionWaiverDialogOpen(false);
    setSelectedSchemeForApproval(null);
    setSelectedSourceTable(null);
    setSelectedFeeRows([]);
    setSelectedFeeFineRows([]);
  };

  const handleCloseDeletePaymentSchemesDialog = () => {
    setIsDeletePaymentSchemesDialogOpen(false);
    setSelectedFeeRows([]);
    setSelectedFeeFineRows([]);
  };

  const handleDownloadReceiptClick = (
    transaction: StudentTransactionMatrixItem
  ) => {
    fetchAndHandleReceiptPdf(transaction, 'download');
  };

  const handleFeeDetailsRowSelection = (
    newSelection: GridRowSelectionModel
  ) => {
    setSelectedFeeRows(
      normalizeSelection(
        newSelection,
        feeDetailsData.map((r) => r._id)
      )
    );
  };

  const handleFeeFineDetailsRowSelection = (
    newSelection: GridRowSelectionModel
  ) => {
    setSelectedFeeFineRows(
      normalizeSelection(
        newSelection,
        lateFeeDetails.map((r) => r._id)
      )
    );
  };

  const handleSendReceiptClick = async (
    transaction: StudentTransactionMatrixItem
  ) => {
    if (transaction.receiptNo) {
      setIsSendingReceipt(true);
      try {
        await paymentSchemesApis.sendReceiptPost(transaction.receiptNo);
        onShowToast('Receipt sent successfully!', SnackbarSeverityType.success);
      } catch (error: any) {
        console.error('Error sending receipt:', error);
        onShowToast(
          error?.errors?.[0]?.message || 'Failed to send receipt.',
          SnackbarSeverityType.error
        );
      } finally {
        setIsSendingReceipt(false);
      }
    } else {
      onShowToast(
        'Receipt number not available.',
        SnackbarSeverityType.warning
      );
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      setSelectedFeeRows([]);
      setSelectedFeeFineRows([]);
      setSelectedSourceTable(null);
    }
  };

  const handleUpdateFeeStatusClick = (
    scheme: StudentPaymentSchemeWithMetrics
  ) => {
    setSelectedSchemeForFeeStatusUpdate(scheme);
    setIsUpdateFeeStatusDialogOpen(true);
  };

  const handleViewReceiptClick = (
    transaction: StudentTransactionMatrixItem
  ) => {
    fetchAndHandleReceiptPdf(transaction, 'view');
  };

  return (
    <PageWrapper>
      {isLoadingFeeDetails && <OverlayLoader show={isLoadingFeeDetails} />}
      {isSendingReceipt && <OverlayLoader show={isSendingReceipt} />}
      {isProcessingReceipt && <OverlayLoader show={isProcessingReceipt} />}
      <Box mb='24px'>
        <Box
          display='flex'
          justifyContent='flex-start'
          alignItems='center'
          flexWrap='wrap'
          gap='16px'
          mb='24px'
        >
          <FeeFilters
            componentType={componentType}
            setComponentType={setComponentType}
            componentTypeOptions={componentTypeOptions}
            particulars={particulars}
            setParticulars={setParticulars}
            particularsOptions={particularsOptions}
            academicYear={academicYear}
            setAcademicYear={setAcademicYear}
            academicYearOptions={academicYearOptions}
          />
        </Box>
        <Box
          display='flex'
          justifyContent='flex-end'
          flexWrap='wrap'
          gap='16px'
        >
          {activeTab === 0 && (
            <Box display='flex' gap='16px'>
              <Button
                variant='contained'
                sx={{
                  background: pallet.successMain,
                  '&:hover': {
                    background: pallet.successMain
                  },
                  lineHeight: 1.25
                }}
                startIcon={<Icons.AddOutlined />}
                onClick={() => setIsAddPaymentDialogOpen(true)}
                disabled={
                  selectedFeeRows.length === 0 &&
                  selectedFeeFineRows.length === 0
                }
              >
                Add Payment
              </Button>
              <Button
                variant='contained'
                sx={{
                  background: pallet.successMain,
                  '&:hover': {
                    background: pallet.successMain
                  },
                  lineHeight: 1.25
                }}
                startIcon={<Icons.AddOutlined />}
                onClick={handleAddConcessionWaiverClick}
                disabled={
                  !(
                    (selectedFeeRows.length === 1 &&
                      selectedFeeFineRows.length === 0) ||
                    (selectedFeeFineRows.length === 1 &&
                      selectedFeeRows.length === 0)
                  )
                }
              >
                Add Concession/Waiver
              </Button>
              {userEmail &&
                usersWithConcessionApprovalAccess.includes(userEmail) &&
                (feeDetailsData.length > 0 || lateFeeDetails.length > 0) && (
                  <Button
                    variant='contained'
                    color='info'
                    sx={{
                      background: pallet.successMain,
                      '&:hover': {
                        background: pallet.successMain
                      },
                      lineHeight: 1.25
                    }}
                    startIcon={<Icons.CopySuccessOutlined />}
                    onClick={() =>
                      setIsApproveRejectConcessionWaiverDialogOpen(true)
                    }
                  >
                    Approve/Reject C/W
                  </Button>
                )}
              {userEmail &&
                usersWithDeletePaymentSchemeAccess.includes(userEmail) && (
                  <Button
                    variant='contained'
                    color='error'
                    sx={{
                      background: pallet.red2,
                      '&:hover': {
                        background: pallet.red2
                      },
                      lineHeight: 1.25
                    }}
                    startIcon={<Icons.MinusOutlined />}
                    onClick={() => setIsDeletePaymentSchemesDialogOpen(true)}
                    disabled={selectedFeeRows.length === 0}
                  >
                    Delete Payment Schemes
                  </Button>
                )}
              {userEmail && superAdminUsers.includes(userEmail) && (
                <Button
                  variant='contained'
                  color='error'
                  sx={{
                    background: pallet.red2,
                    '&:hover': {
                      background: pallet.red2
                    },
                    lineHeight: 1.25
                  }}
                  startIcon={<Icons.MinusOutlined />}
                  onClick={() => setIsDeletePendingLateFeeDialogOpen(true)}
                  disabled={
                    selectedFeeFineRows.length === 0 ||
                    lateFeeDetails
                      .filter((fee) => selectedFeeFineRows.includes(fee._id))
                      .filter((data) => (data.pendingLateFee || 0) > 0)
                      .length === 0
                  }
                >
                  Delete Pending Late Fee
                </Button>
              )}
            </Box>
          )}
          {activeTab === 1 &&
            userEmail &&
            superAdminUsers.includes(userEmail) && (
              <Box display='flex' gap='16px'>
                <Button
                  variant='contained'
                  color='error'
                  sx={{
                    background: pallet.red2,
                    '&:hover': {
                      background: pallet.red2
                    },
                    lineHeight: 1.25
                  }}
                  startIcon={<Icons.MinusOutlined />}
                  onClick={() => setIsCancelTransactionsDialogOpen(true)}
                  disabled={paymentDetailsData.length === 0}
                >
                  Cancel Multiple Transactions
                </Button>
              </Box>
            )}
        </Box>
      </Box>
      <StyledTabs value={activeTab} onChange={handleTabChange}>
        <StyledTab
          icon={
            activeTab === 0 ? (
              <Icons.ReceiptDiscountFilled />
            ) : (
              <Icons.ReceiptDiscountBlack />
            )
          }
          iconPosition='start'
          label='Fee Details / Fee Fine Details'
        />
        <StyledTab
          icon={activeTab === 1 ? <Icons.CardFilled /> : <Icons.CardBlack />}
          iconPosition='start'
          label='Payment Details'
        />
      </StyledTabs>
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <FeeDetailsTable
            title='Fee Details'
            feeDetails={feeDetailsData}
            isLoading={isLoadingFeeDetails}
            checkboxSelection={true}
            rowSelectionModel={{
              type: 'include',
              ids: new Set(selectedFeeRows)
            }}
            onRowSelectionModelChange={handleFeeDetailsRowSelection}
            onApproveRejectClick={(scheme) =>
              handleApproveRejectClick(scheme, 'feeDetails')
            }
            onUpdateFeeStatusClick={handleUpdateFeeStatusClick}
          />
          <FeeFineDetailsTable
            title='Fee Fine Details'
            feeDetails={lateFeeDetails}
            isLoading={isLoadingFeeDetails}
            checkboxSelection={true}
            rowSelectionModel={{
              type: 'include',
              ids: new Set(selectedFeeFineRows)
            }}
            onRowSelectionModelChange={handleFeeFineDetailsRowSelection}
            onApproveRejectClick={(scheme) =>
              handleApproveRejectClick(scheme, 'feeFineDetails')
            }
          />
        </Box>
      )}
      {activeTab === 1 && (
        <PaymentDetailsTable
          title='Payment Details'
          feeDetails={paymentDetailsData}
          isLoading={isLoadingFeeDetails}
          checkboxSelection={false}
          onRowSelectionModelChange={() => {}}
          onUpdateStatusClick={(transaction) => {
            setSelectedTransactionForStatusUpdate(transaction);
            setIsUpdatePaymentStatusDialogOpen(true);
          }}
          onViewReceiptClick={handleViewReceiptClick}
          onDownloadReceiptClick={handleDownloadReceiptClick}
          onSendReceiptClick={handleSendReceiptClick}
          onCancelPaymentClick={(transaction) => {
            setSelectedTransactionToCancel(transaction);
            setIsCancelPaymentDialogOpen(true);
          }}
        />
      )}
      <ViewReceiptPdfDialog
        open={isViewReceiptDialogOpen}
        onClose={() => {
          setIsViewReceiptDialogOpen(false);
          setPdfUrlToView(null);
        }}
        pdfUrl={pdfUrlToView}
        isLoading={isProcessingReceipt}
      />
      <AddPaymentDialog
        open={isAddPaymentDialogOpen}
        onClose={handleCloseAddPaymentDialog}
        onSave={fetchFeeDetails}
        selectedFeeDetails={feeDetailsData.filter((fee) =>
          selectedFeeRows.includes(fee._id)
        )}
        selectedFeeFineDetails={lateFeeDetails.filter((fee) =>
          selectedFeeFineRows.includes(fee._id)
        )}
        userDetails={userDetails}
      />
      <AddConcessionWaiverDialog
        open={isAddConcessionWaiverDialogOpen}
        onClose={handleCloseAddConcessionWaiverDialog}
        onSave={fetchFeeDetails}
        studentPaymentSchemes={studentPaymentSchemes}
        selectedPaymentSchemeIds={
          selectedSourceTable === 'feeFineDetails'
            ? selectedFeeFineRows
            : selectedFeeRows
        }
        isFeeFineSelected={selectedSourceTable === 'feeFineDetails'}
        onShowToast={onShowToast}
      />
      <DeletePaymentSchemesDialog
        open={isDeletePaymentSchemesDialogOpen}
        onClose={handleCloseDeletePaymentSchemesDialog}
        onShowToast={onShowToast}
        onUpdate={fetchFeeDetails}
        selectedPaymentSchemes={feeDetailsData.filter((fee) =>
          selectedFeeRows.includes(fee._id)
        )}
      />
      <DeletePendingLateFeeDialog
        open={isDeletePendingLateFeeDialogOpen}
        onClose={() => {
          setIsDeletePendingLateFeeDialogOpen(false);
          setSelectedFeeFineRows([]);
        }}
        onShowToast={onShowToast}
        onUpdate={fetchFeeDetails}
        selectedFeeFineDetails={lateFeeDetails.filter((fee) =>
          selectedFeeFineRows.includes(fee._id)
        )}
      />
      <ApproveRejectConcessionWaiverDialog
        open={isApproveRejectConcessionWaiverDialogOpen}
        onClose={handleCloseApproveRejectDialog}
        studentId={studentId}
        onShowToast={onShowToast}
        onUpdate={fetchFeeDetails}
        allPaymentSchemes={studentPaymentSchemes}
        selectedPaymentScheme={selectedSchemeForApproval}
        isLoadingFeeDetails={isLoadingFeeDetails}
        sourceTable={selectedSourceTable || undefined}
      />
      <CancelTransactionsDialog
        open={isCancelTransactionsDialogOpen}
        onClose={() => setIsCancelTransactionsDialogOpen(false)}
        onShowToast={onShowToast}
        onUpdate={fetchFeeDetails}
        transactions={paymentDetailsData}
      />
      <UpdateFeeStatusDialog
        open={isUpdateFeeStatusDialogOpen}
        onClose={() => setIsUpdateFeeStatusDialogOpen(false)}
        scheme={selectedSchemeForFeeStatusUpdate}
        onUpdate={fetchFeeDetails}
        onShowToast={onShowToast}
      />
      <CancelPaymentDialog
        open={isCancelPaymentDialogOpen}
        onClose={() => setIsCancelPaymentDialogOpen(false)}
        transaction={selectedTransactionToCancel}
        onUpdate={fetchFeeDetails}
        onShowToast={onShowToast}
      />
      <UpdatePaymentStatusDialog
        open={isUpdatePaymentStatusDialogOpen}
        onClose={() => setIsUpdatePaymentStatusDialogOpen(false)}
        transaction={selectedTransactionForStatusUpdate}
        onUpdate={fetchFeeDetails}
        onShowToast={onShowToast}
        paymentSchemeMatrix={
          selectedTransactionForStatusUpdate
            ? studentPaymentSchemes.find(
                (scheme) =>
                  scheme._id ===
                  selectedTransactionForStatusUpdate.paymentSchemeId
              ) || null
            : null
        }
      />
    </PageWrapper>
  );
};
